import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import SiteNav from "../components/SiteNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/services`).then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const discovery = services.find((s) => !s.requires_consultation);
  const pillars = services.filter((s) => s.requires_consultation);

  return (
    <div className="grain min-h-screen bg-[#0a0a0a] text-white">
      <SiteNav />

      <header className="mx-auto max-w-7xl px-6 pt-40 pb-16 md:px-12 md:pt-48">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="mb-6 text-[10px] uppercase tracking-[0.4em] text-white/40"
        >
          Creator Growth • Brand Development • Influencer Networking
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24, filter: "blur(12px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-lux text-5xl font-light leading-none tracking-tight sm:text-6xl md:text-7xl"
        >
          The Four Pillars
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="mt-8 max-w-2xl text-sm font-light leading-relaxed tracking-wide text-white/50"
        >
          We help influencers grow their audiences, strengthen their personal brands, increase engagement, and connect with the people and opportunities that take their careers to the next level. Every journey starts with a Discovery Call.
        </motion.p>
      </header>

      {/* Discovery Call banner */}
      {discovery && (
        <section className="mx-auto max-w-7xl px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="service-card-discovery-call"
            className="group flex flex-col items-start justify-between gap-6 border border-white/15 bg-white/[0.02] p-8 transition-all duration-500 hover:border-white/40 md:flex-row md:items-center md:p-10"
          >
            <div className="flex items-start gap-5">
              <Sparkles size={22} strokeWidth={1.2} className="mt-1 text-white/60" />
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-serif-lux text-3xl font-light tracking-tight md:text-4xl">{discovery.name}</h3>
                  <span className="text-[9px] uppercase tracking-[0.2em] text-white/70">Start here</span>
                </div>
                <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-white/60">{discovery.description}</p>
              </div>
            </div>
            <button
              data-testid="book-service-discovery-call"
              onClick={() => navigate(`/booking?service=${encodeURIComponent(discovery.name)}`)}
              className="group/btn inline-flex shrink-0 items-center gap-2 rounded-none bg-white px-8 py-3.5 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80"
            >
              Book Now
              <ArrowUpRight size={15} className="transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
            </button>
          </motion.div>
        </section>
      )}

      {/* Pillars */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-20">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          {pillars.map((s, i) => (
            <motion.div
              key={s.key}
              data-testid={`service-card-${s.key}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.1 }}
              className="group relative flex flex-col justify-between overflow-hidden border border-white/10 bg-white/[0.015] p-10 transition-all duration-500 hover:border-white/30 md:p-12"
              style={{ boxShadow: "none" }}
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{ boxShadow: "0 0 60px rgba(255,255,255,0.05) inset" }}
              />
              <div>
                <span className="font-serif-lux text-sm italic text-white/40">0{i + 1}</span>
                <h3 className="font-serif-lux mt-4 text-4xl font-light tracking-tight md:text-5xl">{s.name}</h3>
                <p className="font-serif-lux mt-2 text-lg italic text-white/50">{s.tagline}</p>
                <p className="mt-5 text-sm font-light leading-relaxed text-white/60">{s.description}</p>

                <ul className="mt-8 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {(s.items || []).map((item, idx) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.05 * idx }}
                      className="flex items-start gap-3 text-sm font-light tracking-wide text-white/75"
                    >
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-white/50 transition-colors group-hover:bg-white" />
                      {item}
                    </motion.li>
                  ))}
                </ul>
              </div>

              <button
                data-testid={`book-service-${s.key}`}
                onClick={() => navigate(`/booking?service=${encodeURIComponent(s.name)}`)}
                className="group/btn mt-10 inline-flex items-center gap-2 self-start text-[11px] uppercase tracking-[0.3em] text-white/70 transition-colors hover:text-white"
              >
                Book Now
                <ArrowUpRight size={15} className="transition-transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Event Experiences */}
      <section className="mx-auto max-w-7xl px-6 pb-32 md:px-12">
        <div className="border-t border-white/10 pt-16">
          <p className="mb-4 text-[10px] uppercase tracking-[0.4em] text-white/40">Beyond the pillars</p>
          <h2 className="font-serif-lux max-w-3xl text-3xl font-light leading-snug tracking-tight md:text-4xl">
            We create and host our own creator-focused networking experiences — bringing together influencers, brands, photographers, videographers and industry professionals.
          </h2>
          <div className="mt-10 flex flex-wrap gap-3">
            {["Tickets", "Sponsorships", "Brand Partnerships", "Vendor Fees", "VIP Experiences"].map((t) => (
              <span key={t} className="border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-white/40 hover:text-white">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
